/**
 * Complete list of all admin permissions in the system
 */

export const PERMISSIONS = {
  // User Management
  USERS_VIEW: 'users:view',
  USERS_SUSPEND: 'users:suspend',
  USERS_ACTIVATE: 'users:activate',
  USERS_DELETE: 'users:delete',
  USERS_EDIT_PRICE: 'users:edit_price',
  
  // Caregiver Management
  CAREGIVERS_VIEW: 'caregivers:view',
  CAREGIVERS_VERIFY: 'caregivers:verify',
  CAREGIVERS_REJECT: 'caregivers:reject',
  
  // Booking Management
  BOOKINGS_VIEW: 'bookings:view',
  BOOKINGS_APPROVE: 'bookings:approve',
  BOOKINGS_CANCEL: 'bookings:cancel',
  BOOKINGS_REFUND: 'bookings:refund',
  
  // Financial Management
  FINANCIAL_VIEW: 'financial:view',
  FINANCIAL_EDIT_COMMISSION: 'financial:edit_commission',
  FINANCIAL_PROCESS_REFUNDS: 'financial:process_refunds',
  FINANCIAL_VIEW_TRANSACTIONS: 'financial:view_transactions',
  FINANCIAL_EXPORT_REPORTS: 'financial:export_reports',
  
  // Platform Settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  SETTINGS_FEATURE_FLAGS: 'settings:feature_flags',
  
  // Content Management
  CONTENT_VIEW: 'content:view',
  CONTENT_CREATE_BLOG: 'content:create_blog',
  CONTENT_EDIT_BLOG: 'content:edit_blog',
  CONTENT_DELETE_BLOG: 'content:delete_blog',
  CONTENT_CREATE_FAQ: 'content:create_faq',
  CONTENT_EDIT_FAQ: 'content:edit_faq',
  CONTENT_DELETE_FAQ: 'content:delete_faq',
  
  // Coupon Management
  COUPONS_VIEW: 'coupons:view',
  COUPONS_CREATE: 'coupons:create',
  COUPONS_EDIT: 'coupons:edit',
  COUPONS_DELETE: 'coupons:delete',
  
  // Support Management
  SUPPORT_VIEW: 'support:view',
  SUPPORT_RESPOND: 'support:respond',
  SUPPORT_CLOSE_TICKETS: 'support:close_tickets',
  
  // Analytics
  ANALYTICS_VIEW: 'analytics:view',
  ANALYTICS_EXPORT: 'analytics:export',
  
  // Role Management (Super Admin only)
  ROLES_VIEW: 'roles:view',
  ROLES_CREATE: 'roles:create',
  ROLES_EDIT: 'roles:edit',
  ROLES_DELETE: 'roles:delete',
  ROLES_ASSIGN: 'roles:assign',
  
  // Offline Jobs
  OFFLINE_JOBS_VIEW: 'offline_jobs:view',
  OFFLINE_JOBS_MANAGE: 'offline_jobs:manage',
  
  // System
  SYSTEM_VIEW_LOGS: 'system:view_logs',
  SYSTEM_CLEAR_CACHE: 'system:clear_cache'
};

/**
 * Predefined admin roles with their permissions
 */
export const PREDEFINED_ROLES = {
  SUPER_ADMIN: {
    id: 'super_admin',
    name: 'Super Admin',
    description: 'Full access to all system features',
    permissions: Object.values(PERMISSIONS), // All permissions
    color: 'red',
    icon: '👑',
    isSystem: true, // Cannot be deleted
    level: 100
  },
  
  ADMIN: {
    id: 'admin',
    name: 'Admin',
    description: 'Full admin access except role management',
    permissions: Object.values(PERMISSIONS).filter(p => !p.startsWith('roles:')),
    color: 'blue',
    icon: '⚙️',
    isSystem: true,
    level: 90
  },
  
  USER_MANAGER: {
    id: 'user_manager',
    name: 'User Manager',
    description: 'Manage users and caregivers',
    permissions: [
      PERMISSIONS.USERS_VIEW,
      PERMISSIONS.USERS_SUSPEND,
      PERMISSIONS.USERS_ACTIVATE,
      PERMISSIONS.USERS_DELETE,
      PERMISSIONS.USERS_EDIT_PRICE,
      PERMISSIONS.CAREGIVERS_VIEW,
      PERMISSIONS.CAREGIVERS_VERIFY,
      PERMISSIONS.CAREGIVERS_REJECT,
      PERMISSIONS.ANALYTICS_VIEW
    ],
    color: 'purple',
    icon: '👥',
    isSystem: true,
    level: 70
  },
  
  FINANCIAL_MANAGER: {
    id: 'financial_manager',
    name: 'Financial Manager',
    description: 'Manage finances, commissions, and refunds',
    permissions: [
      PERMISSIONS.FINANCIAL_VIEW,
      PERMISSIONS.FINANCIAL_EDIT_COMMISSION,
      PERMISSIONS.FINANCIAL_PROCESS_REFUNDS,
      PERMISSIONS.FINANCIAL_VIEW_TRANSACTIONS,
      PERMISSIONS.FINANCIAL_EXPORT_REPORTS,
      PERMISSIONS.BOOKINGS_VIEW,
      PERMISSIONS.BOOKINGS_REFUND,
      PERMISSIONS.ANALYTICS_VIEW,
      PERMISSIONS.ANALYTICS_EXPORT
    ],
    color: 'green',
    icon: '💰',
    isSystem: true,
    level: 80
  },
  
  CONTENT_MANAGER: {
    id: 'content_manager',
    name: 'Content Manager',
    description: 'Manage blog posts, FAQs, and content',
    permissions: [
      PERMISSIONS.CONTENT_VIEW,
      PERMISSIONS.CONTENT_CREATE_BLOG,
      PERMISSIONS.CONTENT_EDIT_BLOG,
      PERMISSIONS.CONTENT_DELETE_BLOG,
      PERMISSIONS.CONTENT_CREATE_FAQ,
      PERMISSIONS.CONTENT_EDIT_FAQ,
      PERMISSIONS.CONTENT_DELETE_FAQ,
      PERMISSIONS.ANALYTICS_VIEW
    ],
    color: 'indigo',
    icon: '📝',
    isSystem: true,
    level: 60
  },
  
  SUPPORT_MANAGER: {
    id: 'support_manager',
    name: 'Support Manager',
    description: 'Handle customer support and tickets',
    permissions: [
      PERMISSIONS.SUPPORT_VIEW,
      PERMISSIONS.SUPPORT_RESPOND,
      PERMISSIONS.SUPPORT_CLOSE_TICKETS,
      PERMISSIONS.USERS_VIEW,
      PERMISSIONS.BOOKINGS_VIEW,
      PERMISSIONS.ANALYTICS_VIEW
    ],
    color: 'yellow',
    icon: '🎧',
    isSystem: true,
    level: 50
  },
  
  MODERATOR: {
    id: 'moderator',
    name: 'Moderator',
    description: 'Limited moderation capabilities',
    permissions: [
      PERMISSIONS.USERS_VIEW,
      PERMISSIONS.USERS_SUSPEND,
      PERMISSIONS.CAREGIVERS_VIEW,
      PERMISSIONS.BOOKINGS_VIEW,
      PERMISSIONS.CONTENT_VIEW,
      PERMISSIONS.SUPPORT_VIEW,
      PERMISSIONS.SUPPORT_RESPOND
    ],
    color: 'gray',
    icon: '🛡️',
    isSystem: true,
    level: 40
  }
};

/**
 * Permission categories for UI organization
 */
export const PERMISSION_CATEGORIES = {
  USER_MANAGEMENT: {
    name: 'User Management',
    icon: '👥',
    permissions: [
      PERMISSIONS.USERS_VIEW,
      PERMISSIONS.USERS_SUSPEND,
      PERMISSIONS.USERS_ACTIVATE,
      PERMISSIONS.USERS_DELETE,
      PERMISSIONS.USERS_EDIT_PRICE
    ]
  },
  CAREGIVER_MANAGEMENT: {
    name: 'Caregiver Management',
    icon: '🤝',
    permissions: [
      PERMISSIONS.CAREGIVERS_VIEW,
      PERMISSIONS.CAREGIVERS_VERIFY,
      PERMISSIONS.CAREGIVERS_REJECT
    ]
  },
  BOOKING_MANAGEMENT: {
    name: 'Booking Management',
    icon: '📅',
    permissions: [
      PERMISSIONS.BOOKINGS_VIEW,
      PERMISSIONS.BOOKINGS_APPROVE,
      PERMISSIONS.BOOKINGS_CANCEL,
      PERMISSIONS.BOOKINGS_REFUND
    ]
  },
  FINANCIAL: {
    name: 'Financial Management',
    icon: '💰',
    permissions: [
      PERMISSIONS.FINANCIAL_VIEW,
      PERMISSIONS.FINANCIAL_EDIT_COMMISSION,
      PERMISSIONS.FINANCIAL_PROCESS_REFUNDS,
      PERMISSIONS.FINANCIAL_VIEW_TRANSACTIONS,
      PERMISSIONS.FINANCIAL_EXPORT_REPORTS
    ]
  },
  SETTINGS: {
    name: 'Platform Settings',
    icon: '⚙️',
    permissions: [
      PERMISSIONS.SETTINGS_VIEW,
      PERMISSIONS.SETTINGS_EDIT,
      PERMISSIONS.SETTINGS_FEATURE_FLAGS
    ]
  },
  CONTENT: {
    name: 'Content Management',
    icon: '📝',
    permissions: [
      PERMISSIONS.CONTENT_VIEW,
      PERMISSIONS.CONTENT_CREATE_BLOG,
      PERMISSIONS.CONTENT_EDIT_BLOG,
      PERMISSIONS.CONTENT_DELETE_BLOG,
      PERMISSIONS.CONTENT_CREATE_FAQ,
      PERMISSIONS.CONTENT_EDIT_FAQ,
      PERMISSIONS.CONTENT_DELETE_FAQ
    ]
  },
  COUPONS: {
    name: 'Coupon Management',
    icon: '🎟️',
    permissions: [
      PERMISSIONS.COUPONS_VIEW,
      PERMISSIONS.COUPONS_CREATE,
      PERMISSIONS.COUPONS_EDIT,
      PERMISSIONS.COUPONS_DELETE
    ]
  },
  SUPPORT: {
    name: 'Support Management',
    icon: '🎧',
    permissions: [
      PERMISSIONS.SUPPORT_VIEW,
      PERMISSIONS.SUPPORT_RESPOND,
      PERMISSIONS.SUPPORT_CLOSE_TICKETS
    ]
  },
  ANALYTICS: {
    name: 'Analytics & Reports',
    icon: '📊',
    permissions: [
      PERMISSIONS.ANALYTICS_VIEW,
      PERMISSIONS.ANALYTICS_EXPORT
    ]
  },
  ROLES: {
    name: 'Role Management',
    icon: '🔐',
    permissions: [
      PERMISSIONS.ROLES_VIEW,
      PERMISSIONS.ROLES_CREATE,
      PERMISSIONS.ROLES_EDIT,
      PERMISSIONS.ROLES_DELETE,
      PERMISSIONS.ROLES_ASSIGN
    ]
  },
  OTHER: {
    name: 'Other',
    icon: '📦',
    permissions: [
      PERMISSIONS.OFFLINE_JOBS_VIEW,
      PERMISSIONS.OFFLINE_JOBS_MANAGE,
      PERMISSIONS.SYSTEM_VIEW_LOGS,
      PERMISSIONS.SYSTEM_CLEAR_CACHE
    ]
  }
};

/**
 * Get permission label
 */
export const getPermissionLabel = (permission) => {
  const labels = {
    // Users
    [PERMISSIONS.USERS_VIEW]: 'View Users',
    [PERMISSIONS.USERS_SUSPEND]: 'Suspend Users',
    [PERMISSIONS.USERS_ACTIVATE]: 'Activate Users',
    [PERMISSIONS.USERS_DELETE]: 'Delete Users',
    [PERMISSIONS.USERS_EDIT_PRICE]: 'Edit Subscription Prices',
    
    // Caregivers
    [PERMISSIONS.CAREGIVERS_VIEW]: 'View Caregivers',
    [PERMISSIONS.CAREGIVERS_VERIFY]: 'Verify Caregivers',
    [PERMISSIONS.CAREGIVERS_REJECT]: 'Reject Caregivers',
    
    // Bookings
    [PERMISSIONS.BOOKINGS_VIEW]: 'View Bookings',
    [PERMISSIONS.BOOKINGS_APPROVE]: 'Approve Bookings',
    [PERMISSIONS.BOOKINGS_CANCEL]: 'Cancel Bookings',
    [PERMISSIONS.BOOKINGS_REFUND]: 'Process Refunds',
    
    // Financial
    [PERMISSIONS.FINANCIAL_VIEW]: 'View Financial Data',
    [PERMISSIONS.FINANCIAL_EDIT_COMMISSION]: 'Edit Commission Rates',
    [PERMISSIONS.FINANCIAL_PROCESS_REFUNDS]: 'Process Refunds',
    [PERMISSIONS.FINANCIAL_VIEW_TRANSACTIONS]: 'View Transactions',
    [PERMISSIONS.FINANCIAL_EXPORT_REPORTS]: 'Export Financial Reports',
    
    // Settings
    [PERMISSIONS.SETTINGS_VIEW]: 'View Settings',
    [PERMISSIONS.SETTINGS_EDIT]: 'Edit Settings',
    [PERMISSIONS.SETTINGS_FEATURE_FLAGS]: 'Manage Feature Flags',
    
    // Content
    [PERMISSIONS.CONTENT_VIEW]: 'View Content',
    [PERMISSIONS.CONTENT_CREATE_BLOG]: 'Create Blog Posts',
    [PERMISSIONS.CONTENT_EDIT_BLOG]: 'Edit Blog Posts',
    [PERMISSIONS.CONTENT_DELETE_BLOG]: 'Delete Blog Posts',
    [PERMISSIONS.CONTENT_CREATE_FAQ]: 'Create FAQs',
    [PERMISSIONS.CONTENT_EDIT_FAQ]: 'Edit FAQs',
    [PERMISSIONS.CONTENT_DELETE_FAQ]: 'Delete FAQs',
    
    // Coupons
    [PERMISSIONS.COUPONS_VIEW]: 'View Coupons',
    [PERMISSIONS.COUPONS_CREATE]: 'Create Coupons',
    [PERMISSIONS.COUPONS_EDIT]: 'Edit Coupons',
    [PERMISSIONS.COUPONS_DELETE]: 'Delete Coupons',
    
    // Support
    [PERMISSIONS.SUPPORT_VIEW]: 'View Support Tickets',
    [PERMISSIONS.SUPPORT_RESPOND]: 'Respond to Tickets',
    [PERMISSIONS.SUPPORT_CLOSE_TICKETS]: 'Close Tickets',
    
    // Analytics
    [PERMISSIONS.ANALYTICS_VIEW]: 'View Analytics',
    [PERMISSIONS.ANALYTICS_EXPORT]: 'Export Reports',
    
    // Roles
    [PERMISSIONS.ROLES_VIEW]: 'View Roles',
    [PERMISSIONS.ROLES_CREATE]: 'Create Roles',
    [PERMISSIONS.ROLES_EDIT]: 'Edit Roles',
    [PERMISSIONS.ROLES_DELETE]: 'Delete Roles',
    [PERMISSIONS.ROLES_ASSIGN]: 'Assign Roles',
    
    // Other
    [PERMISSIONS.OFFLINE_JOBS_VIEW]: 'View Offline Jobs',
    [PERMISSIONS.OFFLINE_JOBS_MANAGE]: 'Manage Offline Jobs',
    [PERMISSIONS.SYSTEM_VIEW_LOGS]: 'View System Logs',
    [PERMISSIONS.SYSTEM_CLEAR_CACHE]: 'Clear Cache'
  };
  
  return labels[permission] || permission;
};

